/**
 * Score strip (presentational, FR-9.2): player names + scores, with presence
 * (FR-2.7) and markers for board control + the active responder. Negative scores
 * are allowed and rendered in red (FR-4.7). No controls — pure display.
 */

import type { ClueState, Player } from '@jeopardy/shared';
import { Avatar } from './Avatar';

export interface ScoreboardProps {
  players: Player[];
  boardControlPlayerId?: string;
  clueState?: ClueState;
  /** Highlight this player's own row (the player view). */
  selfPlayerId?: string;
}

export function Scoreboard({
  players,
  boardControlPlayerId,
  clueState,
  selfPlayerId,
}: ScoreboardProps): React.JSX.Element {
  const sorted = [...players].sort((a, b) => b.score - a.score);
  return (
    <div className="scoreboard">
      <h2 className="scoreboard__title">Scores</h2>
      {sorted.length === 0 && <p className="muted">Waiting for players to join…</p>}
      {sorted.map((p) => {
        const hasControl = p.id === boardControlPlayerId;
        const isActive = clueState?.activePlayerId === p.id;
        const lockedOut = clueState?.lockedOutPlayerIds.includes(p.id) ?? false;
        // #192 (FR-4.10): pulse the row of the player just awarded the points during
        // the correct-answer reveal hold.
        const awarded =
          clueState?.status === 'RESOLVED' &&
          clueState.reveal?.outcome === 'correct' &&
          clueState.reveal.awardedPlayerId === p.id;
        const cls =
          'scoreboard__row' +
          (isActive
            ? ' scoreboard__row--active'
            : hasControl
              ? ' scoreboard__row--control'
              : '') +
          (awarded ? ' scoreboard__row--awarded' : '') +
          (p.connected ? '' : ' scoreboard__row--disconnected');
        return (
          <div key={p.id} className={cls} data-testid={`score-${p.id}`}>
            <span>
              <Avatar name={p.name} avatarUrl={p.avatarUrl} size={24} />
              <span className="scoreboard__name">
                {p.name}
                {p.id === selfPlayerId ? ' (you)' : ''}
              </span>
              <span className="scoreboard__badges">
                {hasControl ? ' • control' : ''}
                {isActive ? ' • buzzed in' : ''}
                {lockedOut ? ' • locked out' : ''}
                {p.connected ? '' : ' • offline'}
              </span>
            </span>
            <span
              className={
                'scoreboard__score' + (p.score < 0 ? ' scoreboard__score--negative' : '')
              }
              data-testid={`score-value-${p.id}`}
            >
              {p.score < 0 ? `-$${Math.abs(p.score)}` : `$${p.score}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}
